import { toast } from 'sonner'
import i18next from 'i18next'

// Default configurations
const DEFAULT_DURATION = 3000
const DEFAULT_MESSAGES = {
  pending: 'Processing...',
  genericSuccess: 'Operation completed successfully',
  genericError: 'An error occurred',
}

/**
 * Coerce toast body to a string. API errors often use `description` as an object, e.g.
 * `{ email: ['Not a valid email address.'] }`, which Sonner cannot render as React children.
 * @param {*} message
 * @param {string} fallback
 * @returns {string}
 */
export function normalizeToastMessage(message, fallback) {
  if (message instanceof Error) {
    return message.message || fallback
  }
  if (message === undefined || message === null || message === '') {
    return fallback
  }
  if (
    typeof message === 'string' ||
    typeof message === 'number' ||
    typeof message === 'boolean'
  ) {
    return String(message)
  }
  if (typeof message === 'object') {
    const lines = []
    for (const [key, val] of Object.entries(message)) {
      if (Array.isArray(val)) {
        val.forEach((item) => {
          const part =
            typeof item === 'string' ? item : JSON.stringify(item)
          lines.push(`${key}: ${part}`)
        })
      } else if (val != null && typeof val === 'object') {
        lines.push(`${key}: ${JSON.stringify(val)}`)
      } else {
        lines.push(`${key}: ${String(val)}`)
      }
    }
    if (lines.length > 0) {
      return lines.join('\n')
    }
    try {
      return JSON.stringify(message)
    } catch {
      return fallback
    }
  }
  return String(message)
}

/**
 * Get common toast options with dynamic position based on language.
 * @returns {Object} Common toast options.
 */
const getToastOptions = (customOptions = {}) => {
  const language = i18next.language || 'en'
  return {
    position: language === 'ar' ? 'bottom-left' : 'bottom-right',
    duration: DEFAULT_DURATION,
    ...customOptions,
  }
}

/**
 * Display a toast notification.
 * @param {Function} toastType - The toast method (e.g., toast.success, toast.error).
 * @param {string} message - The message to display.
 * @param {Object} options - Additional toast options.
 */
const notify = (toastType, message, options = {}) => {
  const fallback =
    toastType === toast.success
      ? DEFAULT_MESSAGES.genericSuccess
      : toastType === toast.error
        ? DEFAULT_MESSAGES.genericError
        : ''
  const text = normalizeToastMessage(message, fallback)
  toastType(text, getToastOptions(options))
}

export const notifySuccess = (message, options = {}) =>
  notify(toast.success, message || DEFAULT_MESSAGES.genericSuccess, options)

export const notifyError = (message, options = {}) =>
  notify(toast.error, message || DEFAULT_MESSAGES.genericError, options)

export const notifyInfo = (message, options = {}) =>
  notify(toast.info, message, options)

export const notifyWarning = (message, options = {}) =>
  notify(toast.warning, message, options)

/**
 * Display a toast notification for promise resolutions.
 * @param {Promise} promise - The promise to monitor.
 * @param {Object} messages - An object containing pending, success, and error messages.
 * @param {Object} options - Additional toast options.
 */
export const notifyPromise = (promise, messages = {}, options = {}) => {
  return toast.promise(
    promise,
    {
      pending: normalizeToastMessage(
        messages.pending,
        DEFAULT_MESSAGES.pending,
      ),
      success: (data) =>
        normalizeToastMessage(
          typeof messages.success === 'function'
            ? messages.success(data)
            : messages.success || DEFAULT_MESSAGES.genericSuccess,
          DEFAULT_MESSAGES.genericSuccess,
        ),
      error: (error) =>
        normalizeToastMessage(
          typeof messages.error === 'function'
            ? messages.error(error)
            : messages.error ||
                `${DEFAULT_MESSAGES.genericError}: ${error?.message || ''}`,
          DEFAULT_MESSAGES.genericError,
        ),
    },
    getToastOptions(options),
  )
}

/**
 * Display a toast notification for loading states.
 * @param {string} message - The loading message.
 * @param {Object} options - Additional toast options.
 * @returns {string} The toast ID.
 */
export const notifyLoading = (message, options = {}) => {
  const text = normalizeToastMessage(message, DEFAULT_MESSAGES.pending)
  return toast.loading(text, {
    ...getToastOptions(options),
    duration: Infinity,
    dismissible: false,
    draggable: false,
  })
}

/**
 * Update a loading toast notification by dismissing it and showing a new one.
 * @param {string} id - The toast ID to dismiss.
 * @param {Object} options - The options for the new toast.
 */
export const updateToast = (id, options = {}) => {
  toast.dismiss(id)

  if (options.type === 'success') {
    notifySuccess(options.content)
  } else if (options.type === 'error') {
    notifyError(options.content)
  } else {
    notifyInfo(options.content)
  }
}
